/**
 * UserAvatar
 *
 * Reusable avatar component that handles pre-signed S3 URLs gracefully.
 * On load failure (expired URL, 403, network) it falls back to initials
 * or a generic User icon. Resets automatically when the URL changes
 * (e.g. after profile refetch provides a fresh pre-signed URL).
 */

import { useCallback, useEffect, useState } from 'react';
import { User } from 'lucide-react';
import { cn } from '@tonnie/foundation';

const SIZE = {
  xs: { container: 'w-6 h-6', icon: 'w-3 h-3', text: 'text-2xs' },
  sm: { container: 'w-8 h-8', icon: 'w-4 h-4', text: 'text-xs' },
  md: { container: 'w-10 h-10', icon: 'w-5 h-5', text: 'text-sm' },
  lg: { container: 'w-16 h-16', icon: 'w-7 h-7', text: 'text-lg' },
  xl: { container: 'w-20 h-20', icon: 'w-8 h-8', text: 'text-xl' },
  '2xl': { container: 'w-32 h-32', icon: 'w-12 h-12', text: 'text-2xl' },
} as const;

type AvatarSize = keyof typeof SIZE;

export interface UserAvatarProps {
  src?: string | null;
  alt?: string;
  name?: string | null;
  size?: AvatarSize;
  className?: string;
}

function getInitials(name?: string | null): string {
  if (!name) return '';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

export function UserAvatar({ src, alt, name, size = 'sm', className }: UserAvatarProps) {
  const s = SIZE[size];
  const [failed, setFailed] = useState(false);
  const handleError = useCallback(() => setFailed(true), []);

  useEffect(() => setFailed(false), [src]);

  const showImage = !!src && !failed;
  const initials = getInitials(name);

  return (
    <div
      className={cn(
        s.container,
        'rounded-full overflow-hidden shrink-0 relative',
        !showImage && 'bg-muted flex items-center justify-center',
        className
      )}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt ?? name ?? 'Avatar'}
          className="w-full h-full object-cover"
          onError={handleError}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
        />
      ) : initials ? (
        <span className={cn(s.text, 'font-semibold text-muted-foreground select-none')}>
          {initials}
        </span>
      ) : (
        <User className={cn(s.icon, 'text-muted-foreground')} />
      )}
    </div>
  );
}
